<template>
    <Spinner v-if="showSpinner" />
    <RouterView />

    <!-- Overlay components-->
    <Dialog
        id="selectorg"
        header="Select your organization"
        v-model:visible="showOrganizationSelector"
        :closable="false"
        :modal="true"
    >
        <Listbox
            :options="organizations"
            optionLabel="name"
            style="width: 15rem"
            @change="selectOrganization"
        />
    </Dialog>
</template>

<script lang="ts" setup>
import Spinner from "@/shared/Spinner.vue";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useOrganizationsManager } from "@/stores/organizations-store";
import { useUserStore } from "@/stores/user-store";
import { ListboxChangeEvent } from "primevue/listbox";

const router = useRouter();
const route = useRoute();
useUserStore();

const { showSelectOrganization, isLoadingOrganizations } =
    useOrganizationManager();
const { organizations } = useOrganizationsManager();
const { isLoadingSteks } = useStekManager();

// Only show the spinner when loading items that are required to show
// the current page. For example, when loading a specific product,
// we might also be loading the list of processes for that product
// to be able to show the menu items for the selected product, however
// we don't need to be blocking the entire page with a loader for that.
//
// So, only show the spinner when we are not able to display anything
// that is of value to the user, or when we cannot show a spinner in
// the subsection that is being loaded.
const showSpinner = computed(() => {
    if (
        route.params.processName ||
        route.params.productName ||
        route.params.domainName
    ) {
        return isLoadingOrganizations.value || isLoadingSteks.value;
    }

    return false;
});

const selectOrganization = (event: ListboxChangeEvent) => {
    if (event.value) {
        router.push({
            name: "organizationPage",
            params: {
                orgName: event.value.name,
            },
        });
    }
};

const showOrganizationSelector = computed(
    () => showSelectOrganization.value && !showSpinner.value
);
</script>

<style></style>
